import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useProfile } from "@/hooks/use-profile";
import { usePracticeSessions } from "@/hooks/use-practice-sessions";
import { useRecentPractice } from "@/hooks/use-recent-practice";
import { useRecentStudied } from "@/hooks/use-recent-studied";
import { useReviewReminder } from "@/hooks/use-review-reminder";
import {
  usePublicSessions,
  type PublicSession,
} from "@/hooks/use-public-sessions";
import {
  useGamification,
  useWeeklyStreak,
} from "@/hooks/use-gamification";
import { SEO } from "@/components/seo/SEO";
import type { TopicDisplaySession } from "@/components/topic/TopicCard";
import {
  HomeHero,
  HomeEmptyState,
  HomeLoadingState,
  HomeStudyingNow,
} from "@/components/home";
import { TodayStrip } from "@/components/home/TodayStrip";
import { ContinuePracticing, type ContinueRow } from "@/components/home/ContinuePracticing";
import { DiscoverTabs } from "@/components/home/DiscoverTabs";
import { TopicOfTheDay } from "@/components/home/TopicOfTheDay";
import { GroupTopicOfTheDay } from "@/components/home/GroupTopicOfTheDay";
import { usePageTour } from "@/components/onboarding/usePageTour";
import { getPageTour } from "@/components/onboarding/page-tours";
import { WelcomeModal } from "@/components/onboarding/WelcomeModal";

const toDisplaySession = (s: PublicSession): TopicDisplaySession => ({
  id: s.id,
  topic: s.topic,
  slug: s.slug,
  created_at: s.created_at,
  category: s.category || undefined,
  banner_url: s.banner_url || null,
  view_count: s.view_count ?? 0,
  like_count: s.like_count ?? 0,
  comment_count: s.comment_count ?? 0,
  creator_email: s.creator_email,
  creator_display_name: s.creator_display_name,
  creator_avatar_url: s.creator_avatar_url,
  user_id: s.user_id,
  questions: s.questions,
  vocabulary: s.vocabulary,
});

const Home = () => {
  const navigate = useNavigate();
  const { data: isAuthenticated, userId } = useAuth();

  const homeTour = getPageTour("home");
  const { joyride: homeJoyride, startTour: startHomeTour } = usePageTour({
    routeKey: "home",
    steps: homeTour?.steps ?? [],
    disableAutoStart: !isAuthenticated,
  });

  const { profile } = useProfile();
  const { history, loading: sessionsLoading } = usePracticeSessions(userId ?? null);
  const {
    data: recentPracticeSessionsData,
    isLoading: recentPracticeLoading,
  } = useRecentPractice(userId ?? null, { limit: 5 });
  const recentPracticeSessions = recentPracticeSessionsData ?? [];
  const { data: recentStudiedSessions } = useRecentStudied({ limit: 5 });
  const { vocabularyDue } = useReviewReminder();

  const gamification = useGamification();
  const { weeklyCalendar } = useWeeklyStreak();

  const {
    data: trendingSessionsData,
    isLoading: trendingLoading,
    isFetching: trendingFetching,
  } = usePublicSessions({
    page: 1,
    pageSize: 8,
    sortBy: "trending",
  });

  const {
    data: recentPublicSessionsData,
    isLoading: recentPublicLoading,
    isFetching: recentPublicFetching,
  } = usePublicSessions({
    page: 1,
    pageSize: 8,
    sortBy: "recent",
  });

  const recentTopics = useMemo(() => history.slice(0, 5), [history]);

  const lastStudiedSession = useMemo(() => {
    if (recentStudiedSessions && recentStudiedSessions.length > 0) {
      return recentStudiedSessions[0];
    }
    if (recentPracticeSessions && recentPracticeSessions.length > 0) {
      return recentPracticeSessions[0];
    }
    if (history && history.length > 0) {
      return history[0];
    }
    return null;
  }, [recentStudiedSessions, recentPracticeSessions, history]);

  const trendingTopics = trendingSessionsData?.data || [];
  const recentPublicTopics = recentPublicSessionsData?.data || [];

  /** Pool for the random-question shortcut. */
  const publicTopicPool = useMemo(
    () => [...trendingTopics, ...recentPublicTopics],
    [trendingTopics, recentPublicTopics]
  );

  /**
   * Rows for the "pick up where you left off" list, newest first and
   * de-duplicated across the three sources that describe the same topic
   * (practised, studied, created).
   */
  const continueRows = useMemo<ContinueRow[]>(() => {
    const rows: ContinueRow[] = [];
    const seen = new Set<string>();

    for (const s of recentPracticeSessions) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      rows.push({
        id: s.id,
        topic: s.topic,
        slug: s.slug,
        created_at: s.created_at,
        category: s.category || null,
        banner_url: s.banner_url || null,
        questions: s.questions,
        vocabulary: s.vocabulary,
        touchedAt: s.last_practiced_at || s.created_at,
        activityKind: "practised",
      });
    }
    for (const s of recentStudiedSessions ?? []) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      rows.push({
        id: s.id,
        topic: s.topic,
        slug: s.slug,
        created_at: s.created_at,
        category: s.category || null,
        banner_url: s.banner_url || null,
        questions: s.questions,
        vocabulary: s.vocabulary,
        touchedAt: s.updated_at || s.created_at,
        activityKind: "studied",
      });
    }
    for (const s of recentTopics) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      rows.push({
        id: s.id,
        topic: s.topic,
        slug: s.slug,
        created_at: s.created_at,
        category: s.category || null,
        banner_url: s.banner_url || null,
        questions: s.questions,
        vocabulary: s.vocabulary,
        touchedAt: s.created_at,
        activityKind: "created",
      });
    }

    return rows
      .sort((a, b) =>
        new Date(b.touchedAt ?? b.created_at).getTime() -
        new Date(a.touchedAt ?? a.created_at).getTime()
      )
      .slice(0, 5);
  }, [recentPracticeSessions, recentStudiedSessions, recentTopics]);

  const handlePracticeNow = useCallback(() => {
    if (lastStudiedSession) {
      const slug = lastStudiedSession.slug || lastStudiedSession.id;
      navigate(`/session/${slug}/practice`);
      return;
    }
    if (publicTopicPool.length > 0) {
      const randomTopic =
        publicTopicPool[Math.floor(Math.random() * publicTopicPool.length)];
      const slug = randomTopic.slug || randomTopic.id;
      navigate(`/session/${slug}/practice`);
      return;
    }
    navigate("/topics");
  }, [lastStudiedSession, navigate, publicTopicPool]);

  const handleBrowseTopics = useCallback(() => navigate("/topics"), [navigate]);


  const trendingDisplaySessions = useMemo(
    () => trendingTopics.map(toDisplaySession),
    [trendingTopics],
  );
  const recentPublicDisplaySessions = useMemo(
    () => recentPublicTopics.map(toDisplaySession),
    [recentPublicTopics],
  );

  /**
   * Topic of the day falls back to a deterministic pick from topics Home has
   * already fetched. The get_topic_of_day RPC is the curated source and is
   * preferred whenever it answers; this only covers the case where it does
   * not, so the section never silently disappears. Seeded on the UTC day so
   * every visitor sees the same topic.
   */
  const topicOfDayFallback = useMemo(() => {
    const pool = trendingDisplaySessions.filter((s) => s.slug);
    if (pool.length === 0) return null;
    const seed = Math.floor(
      Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate()) /
        86400000,
    );
    const pick = pool[seed % pool.length];
    return {
      id: pick.id,
      topic: pick.topic,
      slug: pick.slug ?? null,
      banner_url: pick.banner_url ?? null,
      category_name: pick.category ?? null,
      question_count: Array.isArray(pick.questions) ? pick.questions.length : 0,
      word_count: Array.isArray(pick.vocabulary) ? pick.vocabulary.length : 0,
      creator_display_name: pick.creator_display_name ?? null,
      creator_avatar_url: pick.creator_avatar_url ?? null,
    };
  }, [trendingDisplaySessions]);

  const handleShowAllTrending = useCallback(
    () => navigate("/topics/trending"),
    [navigate],
  );
  const handleShowAllRecent = useCallback(
    () => navigate("/topics/recent"),
    [navigate],
  );
  const handleViewSession = useCallback(
    (s: TopicDisplaySession) => navigate(`/session/${s.slug}`),
    [navigate],
  );
  const handlePracticeSession = useCallback(
    (s: TopicDisplaySession) => navigate(`/session/${s.slug}/practice`),
    [navigate],
  );

  /** Same two actions, keyed by slug, for the featured cards that don't hold
      a full TopicDisplaySession. */
  const handlePracticeSlug = useCallback(
    (slug: string) => navigate(`/session/${slug}/practice`),
    [navigate],
  );
  const handleViewSlug = useCallback(
    (slug: string) => navigate(`/session/${slug}`),
    [navigate],
  );

  /** XP banked across the current week, for the strip's progress bar. */
  const weeklyXp = useMemo(() => {
    if (!weeklyCalendar) return 0;
    return weeklyCalendar.reduce((sum, d) => sum + (d.xp_earned ?? 0), 0);
  }, [weeklyCalendar]);

  if (isAuthenticated === undefined) {
    return <HomeLoadingState />;
  }

  return (
    <>
      {homeJoyride}
      {isAuthenticated && (
        <WelcomeModal
          onStartTour={startHomeTour}
          onDismiss={() => {}}
        />
      )}
      <SEO
        title="Home - Your Speaking Practice Dashboard"
        description="Your personalized dashboard for speaking practice. Track your progress, access recent topics, and discover new topics."
        url="/"
        keywords="speaking practice dashboard, language learning, practice topics, vocabulary building"
      />
      {/*
        Calm-focus page shape: task header → live study room → one context
        strip → the primary "where you left off" lesson desk → curated daily
        picks → everything else behind tabs.
      */}
      <div className="min-w-0 space-y-6">
        <HomeHero
          isAuthenticated={!!isAuthenticated}
          displayName={profile?.display_name}
          onCreateTopic={() => navigate("/my-topics")}
          onBrowseTopics={handleBrowseTopics}
          onRandomQuestion={() => navigate("/roll-and-talk")}
        />

        {/* Presence gets its own section: it is the one thing on Home that
            says other people are practising right now, and a footnote under
            the learner's own work communicates none of that. It renders
            nothing when the room is empty. */}
        <HomeStudyingNow />

        {!isAuthenticated && (
          <HomeEmptyState
            isAuthenticated={false}
            onBrowseTopics={handleBrowseTopics}
            onStartPractice={handlePracticeNow}
            onSignIn={() => navigate("/auth")}
          />
        )}

        {isAuthenticated && (
          <TodayStrip
            dueForReview={vocabularyDue ?? 0}
            currentStreak={gamification.currentStreak ?? 0}
            weeklyXp={weeklyXp}
            weeklyCalendar={weeklyCalendar}
          />
        )}

        {isAuthenticated && (
          <ContinuePracticing
            sessions={continueRows}
            loading={recentPracticeLoading || sessionsLoading}
            onBrowse={handleBrowseTopics}
          />
        )}

        {/* Curation and accountability sit between "your stuff" and the
            open-ended browse tabs — they answer "what should I do today?"
            without competing with the resume list above. */}
        {isAuthenticated && (
          <GroupTopicOfTheDay
            enabled={!!userId}
            onOpenTopic={handlePracticeSlug}
            onViewTopic={handleViewSlug}
            onOpenGroup={(groupId) => navigate(`/study-groups/${groupId}`)}
          />
        )}

        <TopicOfTheDay
          fallback={topicOfDayFallback}
          onPractice={handlePracticeSlug}
          onView={handleViewSlug}
        />

        <DiscoverTabs
          trending={{
            sessions: trendingDisplaySessions,
            loading: trendingLoading,
            isFetching: trendingFetching,
            onShowAll: handleShowAllTrending,
          }}
          recent={{
            sessions: recentPublicDisplaySessions,
            loading: recentPublicLoading,
            isFetching: recentPublicFetching,
            onShowAll: handleShowAllRecent,
          }}
          onView={handleViewSession}
          onPractice={handlePracticeSession}
        />
      </div>
    </>
  );
};

export default Home;
